<script lang="ts">
	import { getTenant } from "../getTenant.js";
	import { getAllSolutions } from "../../../core/schema/solutions/solutionsContent.js";
	import type { SolutionContent } from "../../../core/schema/solutions/SolutionContent.js";
	import { SolutionGroup } from "../../../core/schema/SolutionGroup.js";
	import { solutionGroupNameMap } from "../../../core/schema/names/solutionGroupNameMap.js";
	import { Locale, type LocalizedString } from "../../../core/schema/Locale.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import { getSolutionDetailPath } from "../../../core/schema/paths/getSolutionDetailPath.js";
	import { ProductType, productTypeNameMap } from "../../../core/schema/ProductType.js";
	import { getProductsByTypePath } from "../../../core/schema/paths/getProductsByTypePath.js";
	import Icon from "./Icon.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right-long.svg?raw";

	export let headingLevel: 2 | 3 = 3;

	const tenant = getTenant();

	const solutions: SolutionContent[] = getAllSolutions();

	const solutionGroupColumnNameMap: Partial<Record<SolutionGroup, LocalizedString>> = {
		[SolutionGroup.ByIndustry]: {
			[Locale.cs]: "Podle průmyslového odvětví",
			[Locale.en]: "By industry",
		},
	};

	const solutionGroupDescriptionNameMap: Record<SolutionGroup, LocalizedString> = {
		[SolutionGroup.ByIndustry]: {
			[Locale.cs]:
				"Díky zkušenostem z celé řady průmyslových a logistických segmentů dokážeme navrhovat řešení pro specifická odvětví a využívat osvědčené postupy napříč trhy. Propojujeme nejlepší strategie a technologie tak, aby odpovídaly reálným provozním scénářům.",
			[Locale.en]:
				"Thanks to our experience from a wide range of industrial and logistics segments, we can design solutions for specific industries and apply proven practices across markets. We combine the best strategies and technologies so that they match real operational scenarios.",
		},
		[SolutionGroup.ByTechnology]: {
			[Locale.cs]:
				"Nabízíme řadu pokročilých technologií spolu s odbornými znalostmi v oblasti systémového inženýrství a integrace, které pomáhají provozům řešit jejich největší logistické výzvy, od maximalizace efektivity práce až po zvýšení hustoty skladování.",
			[Locale.en]:
				"We offer a range of advanced technologies together with expertise in systems engineering and integration that help operations tackle their biggest logistics challenges – from maximizing labor efficiency to increasing storage density.",
		},
		[SolutionGroup.ByDesignation]: {
			[Locale.cs]:
				"Ať už váš provoz řeší výzvy spojené s e-commerce plněním, zpracováním vratek, omnichannel distribucí nebo provozem mikrofulfillment center, řešení LogTech pomáhají zvyšovat výkon, stabilitu a kvalitu služeb již dnes.",
			[Locale.en]:
				"Whether your operation is tackling the challenges of e-commerce fulfillment, returns processing, omnichannel distribution or running micro-fulfillment centers, LogTech solutions help increase performance, stability and service quality today.",
		},
		[SolutionGroup.ByProduct]: {
			[Locale.cs]: "Vyberte řešení podle konkrétního produktu, který nejlépe odpovídá vašim potřebám.",
			[Locale.en]: "Select a solution based on the specific product that best fits your needs.",
		},
	};

	type GroupedSolutions = Partial<Record<SolutionGroup, SolutionContent[]>>;

	const displayOrder: SolutionGroup[] = [
		SolutionGroup.ByDesignation,
		SolutionGroup.ByTechnology,
		SolutionGroup.ByIndustry,
		SolutionGroup.ByProduct,
	];

	const groupedSolutions: GroupedSolutions = displayOrder.reduce<GroupedSolutions>(
		(grouped: GroupedSolutions, solutionGroup: SolutionGroup) => ({
			...grouped,
			[solutionGroup]: solutions.filter(({ group }: SolutionContent) => group === solutionGroup),
		}),
		{},
	);

	const productTypes: ProductType[] = Object.values(ProductType);

	const itemCounts: Record<SolutionGroup, number> = Object.fromEntries(
		displayOrder.map((solutionGroup: SolutionGroup) => [
			solutionGroup,
			solutionGroup === SolutionGroup.ByProduct ? productTypes.length : (groupedSolutions[solutionGroup]?.length ?? 0),
		]),
	) as Record<SolutionGroup, number>;

	const visibleGroups: SolutionGroup[] = displayOrder.filter(
		(solutionGroup: SolutionGroup) => itemCounts[solutionGroup] > 0,
	);

	/*
	 * On xl the lists have different heights, so the vertical line between two neighbouring columns is drawn
	 * by the longer one only (the right one on a tie). This way no two borders overlap.
	 */
	function getListBorderClasses(index: number): string {
		const group = visibleGroups[index];
		if (group === undefined) {
			throw new Error("Index out of bounds");
		}
		const count = itemCounts[group];
		const previous = visibleGroups[index - 1];
		const next = visibleGroups[index + 1];
		const hasLeftBorder = previous === undefined || count >= itemCounts[previous];
		const hasRightBorder = next === undefined || count > itemCounts[next];
		return [hasLeftBorder && "xl:border-l", hasRightBorder && "xl:border-r"].filter(Boolean).join(" ");
	}
</script>

<div class="border-lttertiary border-t">
	<div class="mx-auto max-w-[120rem]">
		<div class="mx-4 md:mx-[5.125rem] lg:mx-[8.125rem]" role="table">
			<div
				class="border-lttertiary grid grid-cols-1 md:grid-cols-2 md:border-l xl:grid-cols-4 xl:grid-rows-[auto_1fr] xl:border-0"
				role="rowgroup"
			>
				{#each visibleGroups as solutionGroup, index (solutionGroup)}
					{@const solutionsByGroup = groupedSolutions[solutionGroup] ?? []}
					{@const isProductGroup = solutionGroup === SolutionGroup.ByProduct}
					<div
						class="border-lttertiary flex flex-col border-x border-b md:row-span-2 md:grid md:grid-rows-subgrid md:border-l-0 xl:contents"
						role="row"
					>
						<div
							role="rowheader"
							class="bg-lttertiary-200 border-lttertiary flex min-h-[11.5625rem] flex-col gap-4 border-b p-6 xl:row-start-1 xl:border-b xl:border-r {index ===
							0
								? 'xl:border-l'
								: ''}"
						>
							<svelte:element
								this={`h${headingLevel}`}
								class="text-ltprimary md:text-2md mb-0 min-h-[2lh] text-[1.1875rem] font-normal uppercase leading-[1.3] tracking-wide"
							>
								{nbspify(
									(solutionGroupColumnNameMap[solutionGroup] ?? solutionGroupNameMap[solutionGroup])[tenant.locale],
								)}
							</svelte:element>
							<p class="!mb-0 break-words !text-xs font-light leading-tight tracking-normal">
								{nbspify(solutionGroupDescriptionNameMap[solutionGroup][tenant.locale])}
							</p>
						</div>

						<div
							class="divide-lttertiary border-lttertiary flex flex-col divide-y xl:row-start-2 xl:self-start xl:border-b {getListBorderClasses(
								index,
							)}"
						>
							{#if isProductGroup}
								{#each productTypes as productType (productType)}
									<div role="cell">
										<a
											href="/{getProductsByTypePath(productType)[tenant.locale]}"
											class="group/item hover:bg-lttertiary-200 flex h-[3.125rem] items-center justify-between gap-4 px-4 transition-colors duration-200 md:px-6"
										>
											<span
												class="text-ltsecondary !mb-0 !text-xs font-normal leading-tight tracking-normal transition-colors md:leading-none"
											>
												{nbspify(productTypeNameMap[productType][tenant.locale])}
											</span>
											<Icon
												icon={arrowRight}
												class="text-ltprimary-300 group-hover/item:text-ltprimary-150 size-4 shrink-0 opacity-0 transition-[opacity,color] duration-200 group-hover/item:opacity-100"
											/>
										</a>
									</div>
								{/each}
							{:else}
								{#each solutionsByGroup as solution (solution.slug)}
									<div role="cell">
										<a
											href="/{getSolutionDetailPath(solution)[tenant.locale]}"
											class="group/item hover:bg-lttertiary-200 flex h-[3.125rem] items-center justify-between gap-4 px-4 transition-colors duration-200 md:px-6"
										>
											<span
												class="text-ltsecondary !mb-0 !text-xs font-normal leading-tight tracking-normal transition-colors md:leading-none"
											>
												{nbspify(solution.name[tenant.locale])}
											</span>
											<Icon
												icon={arrowRight}
												class="text-ltprimary-300 group-hover/item:text-ltprimary-150 size-4 shrink-0 opacity-0 transition-[opacity,color] duration-200 group-hover/item:opacity-100"
											/>
										</a>
									</div>
								{/each}
							{/if}
						</div>
					</div>
				{/each}
			</div>
		</div>
	</div>
</div>
